import React, {MouseEvent as SyntheticMouseEvent, useCallback, useMemo, useRef} from 'react';
import cx from 'classnames';
import {Column, Form} from '../../../../stores/Form';
import {Flex} from '@priority-software/priority-style-react';
import {HeaderCellSort} from '../../../Form/components/HeaderCellSort';
import {HeaderCellFilter} from '../../../Form/components/HeaderCellFilter';
import styles from './styles.module.scss';
import {INITIAL_COLUMN_WIDTH} from '../Table';
import {isActiveFilter} from '../../../Form/utils/filters';
import {getTopDialogZindex} from '../../../../_shared/GWTBridge/invoke/priModalDlg';
import {useWidthContext} from '../Table/WidthController/useWidthContext';
import {ModernTooltip} from '../../../../components/Common/Tooltip/ModernTooltip';

export type Props = {
    isDisabledResize?: boolean;
    column: Column;
    currentWidth: number;
    resizeColumnId: number | undefined;
    form?: Form;
    setInitialResizeX: (value: number) => void;
    setInitialWidth: (value: number) => void;
    setCurrentWidth: (value: number) => void;
    setResizeColumnId: (value: number) => void;
    handleSort: (
        e: SyntheticMouseEvent<HTMLDivElement | HTMLSpanElement>,
        columnId: number,
        sortedColumn: any
    ) => void;
    showFilter: boolean;
};

export const HeaderCell = ({
    column,
    currentWidth,
    resizeColumnId,
    form,
    isDisabledResize,
    setInitialResizeX,
    setCurrentWidth,
    setResizeColumnId,
    setInitialWidth,
    handleSort,
    showFilter
}: Props) => {
    const sortedColumn = useMemo(
        () => form?.sort?.find(item => Number(item.ColumnID) === column.ID),
        [form?.sort, column.ID]
    );
    const contentRef = useRef<HTMLDivElement>(null);
    const getCellWrap = useCallback(() => contentRef.current, []);
    const hasActiveFilter = form ? isActiveFilter(column) : false;

    const {width} = useWidthContext();

    const columnStyleFunc = (column: Column) => ({
        width: `${resizeColumnId === column.ID ? currentWidth : width[column.ID.toString()]}px`
    });

    const columnStyle = columnStyleFunc(column);

    const handleCellClick = useCallback(
        (e: SyntheticMouseEvent<HTMLDivElement>) => {
            e.stopPropagation();
            handleSort(e, column.ID as number, sortedColumn);
        },
        [handleSort, sortedColumn]
    );

    const handleMouseDown = (event: SyntheticMouseEvent<HTMLDivElement>) => {
        const columnId = column.ID as number;
        if (resizeColumnId === undefined) {
            setResizeColumnId(columnId);
            setInitialWidth(width[columnId] || INITIAL_COLUMN_WIDTH);
            setCurrentWidth(width[columnId] || INITIAL_COLUMN_WIDTH);
            setInitialResizeX(event.screenX);
        }
    };

    return (
        <div style={columnStyle} className={styles.cell} onClick={handleCellClick}>
            <Flex>
                <Flex direction={'row'} style={{...columnStyle, minWidth: '100%'}} ref={contentRef}>
                    <ModernTooltip
                        tooltip={column.Title}
                        arrowOffset={12}
                        position="top-start"
                        tooltipOffset={8}
                        wrapperClassname={styles.headerCellTitle}
                    >
                        {column.Title}
                    </ModernTooltip>
                    {!!sortedColumn && (
                        <HeaderCellSort
                            componentClasses={styles.sortIcon}
                            isDesc={!!sortedColumn?.Desc}
                            onClick={e => handleSort(e, column.ID as number, sortedColumn)}
                            disabled={!!resizeColumnId}
                        />
                    )}
                    {showFilter && (
                        <HeaderCellFilter
                            disabled={!!resizeColumnId}
                            componentClasses={cx(styles.filterContainer, {
                                [styles.isActive]: hasActiveFilter
                            })}
                            getCellWrap={getCellWrap}
                            column={column}
                            zIndex={getTopDialogZindex()}
                        />
                    )}
                </Flex>
                <div
                    onMouseDown={handleMouseDown}
                    className={cx(styles.resize, {
                        [styles.resizeDisabled]: isDisabledResize
                    })}
                />
            </Flex>
        </div>
    );
};
